import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  CalendarDaysIcon,
  ClockIcon,
  UserIcon,
  ChevronRightIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationCircleIcon,
  ClockIcon as PendingIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useTodayAppointments, TodayAppointment } from '../../hooks/useTodayAppointments';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import toast from 'react-hot-toast';

export function TodayAppointments() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { 
    appointments, 
    stats, 
    isLoading, 
    error, 
    refetch,
    markAsCompleted,
    markAsCancelled,
    markAsConfirmed,
    markAsPending
  } = useTodayAppointments();

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [selectedAppointment, setSelectedAppointment] = useState<TodayAppointment | null>(null);
  const [isUpdating, setIsUpdating] = useState<string | null>(null);
  
  // Estados para secciones desplegables
  const [isOngoingExpanded, setIsOngoingExpanded] = useState(true);
  const [isNextExpanded, setIsNextExpanded] = useState(true);
  const [isAllExpanded, setIsAllExpanded] = useState(true);

  const handleAction = async (
    appointmentId: string, 
    action: () => Promise<boolean>, 
    successMessage: string, 
    errorMessage: string
  ) => {
    setIsUpdating(appointmentId);
    try {
      await action();
      toast.success(successMessage, {
        duration: 3000,
        position: 'top-right',
        style: {
          background: '#10b981',
          color: '#ffffff',
          fontWeight: '500',
        },
        iconTheme: {
          primary: '#ffffff',
          secondary: '#10b981',
        },
      });
    } catch (error) {
      console.error('Error executing action:', error);
      toast.error(errorMessage, {
        duration: 4000,
        position: 'top-right',
        style: {
          background: '#ef4444',
          color: '#ffffff',
          fontWeight: '500',
        },
        iconTheme: {
          primary: '#ffffff',
          secondary: '#ef4444',
        },
      });
    } finally {
      setIsUpdating(null);
    }
  };

  const handleCancelClick = (appointment: TodayAppointment) => {
    setSelectedAppointment(appointment);
    setShowCancelModal(true);
  };

  const handleCancel = async () => {
    if (!selectedAppointment) return;
    
    await handleAction(
      selectedAppointment.id,
      () => markAsCancelled(selectedAppointment.id, cancelReason || 'Cancelada desde dashboard'),
      `Cita de ${selectedAppointment.patientName} cancelada correctamente`,
      'Error al cancelar la cita. Inténtalo de nuevo.'
    );
    setShowCancelModal(false);
    setCancelReason('');
    setSelectedAppointment(null);
  };

  const getStatusConfig = (status: TodayAppointment['status']) => {
    const configs = {
      PENDIENTE: {
        icon: PendingIcon,
        color: 'text-yellow-600 dark:text-yellow-400',
        bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
        borderColor: 'border-yellow-200 dark:border-yellow-800',
        label: 'Pendiente'
      },
      CONFIRMADA: {
        icon: CheckCircleIcon,
        color: 'text-blue-600 dark:text-blue-400',
        bgColor: 'bg-blue-50 dark:bg-blue-900/20',
        borderColor: 'border-blue-200 dark:border-blue-800',
        label: 'Confirmada'
      },
      REALIZADA: {
        icon: CheckCircleIcon,
        color: 'text-green-600 dark:text-green-400',
        bgColor: 'bg-green-50 dark:bg-green-900/20',
        borderColor: 'border-green-200 dark:border-green-800',
        label: 'Completada'
      },
      CANCELADA: {
        icon: XCircleIcon,
        color: 'text-red-600 dark:text-red-400',
        bgColor: 'bg-red-50 dark:bg-red-900/20',
        borderColor: 'border-red-200 dark:border-red-800',
        label: 'Cancelada'
      },
      NO_ACUDE: {
        icon: XCircleIcon,
        color: 'text-orange-600 dark:text-orange-400',
        bgColor: 'bg-orange-50 dark:bg-orange-900/20',
        borderColor: 'border-orange-200 dark:border-orange-800',
        label: 'No Acude'
      },
      REPROGRAMADA: {
        icon: ClockIcon,
        color: 'text-purple-600 dark:text-purple-400',
        bgColor: 'bg-purple-50 dark:bg-purple-900/20',
        borderColor: 'border-purple-200 dark:border-purple-800',
        label: 'Reprogramada'
      }
    };
    return configs[status] || configs.PENDIENTE;
  };

  const formatTime = (time: string) => {
    return time.substring(0, 5);
  };

  const getCurrentTimeStatus = (appointment: TodayAppointment) => {
    const now = new Date();
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    const startTime = formatTime(appointment.timeStart);
    const endTime = formatTime(appointment.timeEnd);

    if (currentTime < startTime) {
      return 'upcoming';
    } else if (currentTime >= startTime && currentTime <= endTime) {
      return 'ongoing';
    } else {
      return 'past';
    }
  };

  const goToAppointments = () => {
    navigate('/appointments');
  };

  const today = new Date();
  const todayFormatted = today.toLocaleDateString('es-ES', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  if (isLoading) {
    return (
      <div className="card">
        <div className="flex items-center justify-center h-48">
          <LoadingSpinner size="md" text="Cargando citas de hoy..." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="flex items-center justify-center h-48 text-center">
          <div>
            <ExclamationCircleIcon className="mx-auto h-12 w-12 text-red-500 mb-4" />
            <p className="text-gray-600 dark:text-gray-400 mb-4">Error al cargar las citas</p>
            <button
              onClick={() => refetch()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Reintentar
            </button>
          </div>
        </div>
      </div>
    );
  }

  const getActionButtons = (appointment: TodayAppointment) => {
    const isLoading = isUpdating === appointment.id;
    
    switch (appointment.status) {
      case 'PENDIENTE':
        return [
          <button
            key="confirm"
            onClick={() => handleAction(
              appointment.id,
              () => markAsConfirmed(appointment.id), 
              `Cita de ${appointment.patientName} confirmada`,
              'Error al confirmar la cita'
            )}
            disabled={isLoading}
            className="px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 text-xs font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            title="Confirmar cita"
          >
            {isLoading ? (
              <div className="w-3 h-3 border border-blue-400 border-t-blue-700 rounded-full animate-spin" />
            ) : (
              <CheckCircleIcon className="w-3 h-3" />
            )}
            Confirmar
          </button>,
          <button
            key="complete"
            onClick={() => handleAction(
              appointment.id,
              () => markAsCompleted(appointment.id), 
              `Cita de ${appointment.patientName} completada`,
              'Error al completar la cita'
            )}
            disabled={isLoading}
            className="px-3 py-1.5 bg-green-100 hover:bg-green-200 text-green-700 text-xs font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            title="Completar cita"
          >
            {isLoading ? (
              <div className="w-3 h-3 border border-green-400 border-t-green-700 rounded-full animate-spin" />
            ) : (
              <CheckCircleIcon className="w-3 h-3" />
            )}
            Completar
          </button>,
          <button
            key="cancel"
            onClick={() => handleCancelClick(appointment)}
            disabled={isLoading}
            className="px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 text-xs font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            title="Cancelar cita"
          >
            <XCircleIcon className="w-3 h-3" />
            Cancelar
          </button>
        ];

      case 'CONFIRMADA':
        return [
          <button
            key="complete"
            onClick={() => handleAction(
              appointment.id,
              () => markAsCompleted(appointment.id), 
              `Cita de ${appointment.patientName} completada`,
              'Error al completar la cita'
            )}
            disabled={isLoading}
            className="px-3 py-1.5 bg-green-100 hover:bg-green-200 text-green-700 text-xs font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            title="Completar cita"
          >
            {isLoading ? (
              <div className="w-3 h-3 border border-green-400 border-t-green-700 rounded-full animate-spin" />
            ) : (
              <CheckCircleIcon className="w-3 h-3" />
            )}
            Completar
          </button>,
          <button
            key="pending"
            onClick={() => handleAction(
              appointment.id,
              () => markAsPending(appointment.id), 
              `Cita de ${appointment.patientName} marcada como pendiente`,
              'Error al cambiar el estado de la cita'
            )}
            disabled={isLoading}
            className="px-3 py-1.5 bg-yellow-100 hover:bg-yellow-200 text-yellow-700 text-xs font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            title="Marcar como pendiente"
          >
            {isLoading ? (
              <div className="w-3 h-3 border border-yellow-400 border-t-yellow-700 rounded-full animate-spin" />
            ) : (
              <ClockIcon className="w-3 h-3" />
            )}
            Pendiente
          </button>,
          <button
            key="cancel"
            onClick={() => handleCancelClick(appointment)}
            disabled={isLoading}
            className="px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 text-xs font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            title="Cancelar cita"
          >
            <XCircleIcon className="w-3 h-3" />
            Cancelar
          </button>
        ];

      case 'REALIZADA':
        return [
          <button
            key="reactivate"
            onClick={() => handleAction(
              appointment.id,
              () => markAsPending(appointment.id), 
              `Cita de ${appointment.patientName} marcada como pendiente`,
              'Error al cambiar el estado de la cita'
            )}
            disabled={isLoading}
            className="px-3 py-1.5 bg-yellow-100 hover:bg-yellow-200 text-yellow-700 text-xs font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            title="Marcar como pendiente"
          >
            {isLoading ? (
              <div className="w-3 h-3 border border-yellow-400 border-t-yellow-700 rounded-full animate-spin" />
            ) : (
              <ClockIcon className="w-3 h-3" />
            )}
            Reactivar
          </button>
        ];

      case 'CANCELADA':
        return [
          <button
            key="reactivate"
            onClick={() => handleAction(
              appointment.id,
              () => markAsPending(appointment.id), 
              `Cita de ${appointment.patientName} reactivada`,
              'Error al reactivar la cita'
            )}
            disabled={isLoading}
            className="px-3 py-1.5 bg-yellow-100 hover:bg-yellow-200 text-yellow-700 text-xs font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            title="Reactivar como pendiente"
          >
            {isLoading ? (
              <div className="w-3 h-3 border border-yellow-400 border-t-yellow-700 rounded-full animate-spin" />
            ) : (
              <ClockIcon className="w-3 h-3" />
            )}
            Reactivar
          </button>,
          <button
            key="confirm"
            onClick={() => handleAction(
              appointment.id,
              () => markAsConfirmed(appointment.id), 
              `Cita de ${appointment.patientName} confirmada`,
              'Error al confirmar la cita'
            )}
            disabled={isLoading}
            className="px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 text-xs font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            title="Confirmar cita"
          >
            {isLoading ? (
              <div className="w-3 h-3 border border-blue-400 border-t-blue-700 rounded-full animate-spin" />
            ) : (
              <CheckCircleIcon className="w-3 h-3" />
            )}
            Confirmar
          </button>
        ];

      case 'NO_ACUDE':
      case 'REPROGRAMADA':
        return [
          <button
            key="reactivate"
            onClick={() => handleAction(
              appointment.id,
              () => markAsPending(appointment.id), 
              `Cita de ${appointment.patientName} reactivada`,
              'Error al reactivar la cita'
            )}
            disabled={isLoading}
            className="px-3 py-1.5 bg-yellow-100 hover:bg-yellow-200 text-yellow-700 text-xs font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
            title="Reactivar como pendiente"
          >
            {isLoading ? (
              <div className="w-3 h-3 border border-yellow-400 border-t-yellow-700 rounded-full animate-spin" />
            ) : (
              <ClockIcon className="w-3 h-3" />
            )}
            Reactivar
          </button>
        ];

      default:
        return [];
    }
  };

  // Funciones helper para organizar las citas
  const getOngoingAppointments = () => {
    return appointments.filter(appointment => {
      const timeStatus = getCurrentTimeStatus(appointment);
      return timeStatus === 'ongoing' && appointment.status !== 'REALIZADA' && appointment.status !== 'CANCELADA';
    });
  };

  const getNextAppointment = () => {
    return appointments.find(appointment => appointment.isNext);
  };

  const ongoingAppointments = getOngoingAppointments();
  const nextAppointment = getNextAppointment();

  // Componente para secciones desplegables
  const CollapsibleSection = ({ 
    title, 
    count, 
    isExpanded, 
    onToggle, 
    children, 
    icon: IconComponent,
    color = "text-gray-600 dark:text-gray-400"
  }: {
    title: string;
    count: number;
    isExpanded: boolean;
    onToggle: () => void;
    children: React.ReactNode;
    icon: React.ComponentType<{ className?: string }>;
    color?: string;
  }) => (
    <div className="mb-6">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <IconComponent className={`w-5 h-5 ${color}`} />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {title} ({count})
          </h3>
        </div>
        <ChevronRightIcon 
          className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} 
        />
      </button>
      {isExpanded && (
        <div className="mt-3">
          {children}
        </div>
      )}
    </div>
  );

  // Función para renderizar una cita individual
  const renderAppointment = (appointment: TodayAppointment) => {
    const statusConfig = getStatusConfig(appointment.status);
    const StatusIcon = statusConfig.icon;
    const timeStatus = getCurrentTimeStatus(appointment);

    return (
      <div
        key={appointment.id}
        className={`
          relative p-4 border rounded-lg transition-all duration-200 hover:shadow-md overflow-hidden
          ${statusConfig.bgColor} ${statusConfig.borderColor}
          ${appointment.isNext ? 'ring-2 ring-blue-300 dark:ring-blue-600' : ''}
          ${timeStatus === 'ongoing' ? 'ring-2 ring-green-300 dark:ring-green-600' : ''}
        `}
      >
        {appointment.isNext && (
          <div className="absolute top-2 right-2">
            <div className="px-2 py-1 bg-blue-600 text-white text-xs font-medium rounded-full shadow-sm">
              Próxima
            </div>
          </div>
        )}
        
        {timeStatus === 'ongoing' && (
          <div className="absolute top-2 left-2">
            <div className="px-2 py-1 bg-green-600 text-white text-xs font-medium rounded-full animate-pulse shadow-sm">
              En curso
            </div>
          </div>
        )}

        <div className={`${appointment.isNext || timeStatus === 'ongoing' ? 'mt-6' : ''}`}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <div className={`p-2 rounded-lg ${statusConfig.bgColor}`}>
                <StatusIcon className={`w-5 h-5 ${statusConfig.color}`} />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {appointment.patientName}
                  </span>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusConfig.bgColor} ${statusConfig.color}`}>
                    {statusConfig.label}
                  </span>
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 truncate">
                  {appointment.treatmentName}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-500 truncate">
                  Con {appointment.professionalName}
                </div>
              </div>
            </div>

            <div className="flex flex-col items-end gap-2 flex-shrink-0">
              <div className="font-mono text-sm font-medium text-gray-900 dark:text-gray-100">
                {appointment.time}
              </div>
              {timeStatus === 'past' && appointment.status !== 'REALIZADA' && appointment.status !== 'CANCELADA' && (
                <div className="text-xs text-red-500">
                  Hora pasada
                </div>
              )}
            </div>
          </div>
          
          {/* Botones de acción */}
          <div className="mt-3 flex justify-end">
            <div className="flex flex-wrap gap-1 sm:gap-2">
              {getActionButtons(appointment)}
            </div>
          </div>
        </div>

        {appointment.notes && (
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              📝 {appointment.notes}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="card">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <CalendarDaysIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                Citas de Hoy
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 capitalize">
                {todayFormatted}
              </p>
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => refetch()}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            title="Actualizar"
          >
            <ArrowPathIcon className="w-5 h-5" />
          </button>
          <button
            onClick={goToAppointments}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
          >
            Ver todas
            <ChevronRightIcon className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6">
        <div className="p-3 md:p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
          <div className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100">
            {stats.total}
          </div>
          <div className="text-xs md:text-sm text-gray-500 dark:text-gray-400">Total</div>
        </div>
        <div className="p-3 md:p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <div className="text-xl md:text-2xl font-bold text-blue-600 dark:text-blue-400">
            {stats.confirmed}
          </div>
          <div className="text-xs md:text-sm text-gray-500 dark:text-gray-400">Confirmadas</div>
        </div>
        <div className="p-3 md:p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
          <div className="text-xl md:text-2xl font-bold text-green-600 dark:text-green-400">
            {stats.completed}
          </div>
          <div className="text-xs md:text-sm text-gray-500 dark:text-gray-400">Completadas</div>
        </div>
        <div className="p-3 md:p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
          <div className="text-xl md:text-2xl font-bold text-yellow-600 dark:text-yellow-400">
            {stats.pending}
          </div>
          <div className="text-xs md:text-sm text-gray-500 dark:text-gray-400">Pendientes</div>
        </div>
      </div>

      {/* Próxima cita */}
      {stats.nextAppointment && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
            Próxima Cita
          </h3>
          <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                  <ClockIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <div className="font-semibold text-gray-900 dark:text-gray-100">
                    {stats.nextAppointment.patientName}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {stats.nextAppointment.time} • {stats.nextAppointment.treatmentName}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-500">
                    Con {stats.nextAppointment.professionalName}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm font-medium text-blue-600 dark:text-blue-400">
                  ¡Próxima!
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Secciones Desplegables */}
      {appointments.length === 0 ? (
        <div className="text-center py-12">
          <CalendarDaysIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500 mb-4" />
          <p className="text-gray-500 dark:text-gray-400 text-lg font-medium mb-2">
            No hay citas programadas para hoy
          </p>
          <p className="text-gray-400 dark:text-gray-500 text-sm">
            ¡Perfecto día para ponerse al día!
          </p>
        </div>
      ) : (
        <>
          {/* Sección: En Curso */}
          {ongoingAppointments.length > 0 && (
            <CollapsibleSection
              title="En Curso"
              count={ongoingAppointments.length}
              isExpanded={isOngoingExpanded}
              onToggle={() => setIsOngoingExpanded(!isOngoingExpanded)}
              icon={ClockIcon}
              color="text-green-600 dark:text-green-400"
            >
              <div className="space-y-3">
                {ongoingAppointments.map(renderAppointment)}
              </div>
            </CollapsibleSection>
          )}

          {/* Sección: Próxima Cita 
          {nextAppointment && (
            <CollapsibleSection
              title="Próxima Cita"
              count={1}
              isExpanded={isNextExpanded}
              onToggle={() => setIsNextExpanded(!isNextExpanded)}
              icon={ClockIcon}
              color="text-blue-600 dark:text-blue-400"
            >
              <div className="space-y-3">
                {renderAppointment(nextAppointment)}
              </div>
            </CollapsibleSection>
          )}*/}

          {/* Sección: Todas las Citas */}
          <CollapsibleSection
            title="Todas las Citas"
            count={appointments.length}
            isExpanded={isAllExpanded}
            onToggle={() => setIsAllExpanded(!isAllExpanded)}
            icon={CalendarDaysIcon}
            color="text-gray-600 dark:text-gray-400"
          >
            <div 
              className="space-y-3 max-h-96 overflow-y-auto pr-3 -mr-3 border-l border-gray-100 dark:border-gray-700 pl-1 relative"
              style={{
                scrollbarWidth: 'thin',
                scrollbarColor: '#cbd5e1 #f1f5f9',
                background: 'linear-gradient(to bottom, rgba(249,250,251,0.4) 0%, transparent 10%, transparent 90%, rgba(249,250,251,0.4) 100%)'
              }}
            >
              {appointments.map(renderAppointment)}
            </div>
          </CollapsibleSection>
        </>
      )}

      {/* Footer con acceso rápido */}
      {appointments.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={goToAppointments}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
          >
            <CalendarDaysIcon className="w-5 h-5" />
            Gestionar Todas las Citas
            <ChevronRightIcon className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Modal de cancelación */}
      {showCancelModal && selectedAppointment && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4 text-center">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            
            <div className="relative inline-block w-full max-w-lg bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20 sm:mx-0 sm:h-10 sm:w-10">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1 min-w-0">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
                    Cancelar Cita
                  </h3>
                  <div className="mt-4">
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                      ¿Estás seguro de que deseas cancelar la cita de <strong>{selectedAppointment.patientName}</strong>?
                    </p>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Motivo de cancelación (opcional)
                      </label>
                      <textarea
                        value={cancelReason}
                        onChange={(e) => setCancelReason(e.target.value)}
                        rows={3}
                        className="block w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        placeholder="Ej: Paciente enfermo, reagendar para otra fecha..."
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 flex flex-col sm:flex-row-reverse gap-3">
                <button
                  onClick={handleCancel}
                  disabled={isUpdating === selectedAppointment.id}
                  className="w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUpdating === selectedAppointment.id ? 'Cancelando...' : 'Confirmar Cancelación'}
                </button>
                <button
                  onClick={() => {
                    setShowCancelModal(false);
                    setCancelReason('');
                    setSelectedAppointment(null);
                  }}
                  disabled={isUpdating === selectedAppointment.id}
                  className="w-full inline-flex justify-center rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-700 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 